import { Link, useLocation } from 'react-router-dom';
import { Moon, Sun, MapPin, Home, User, Menu, X, Send, Mail, Plus } from 'lucide-react';
import { useTheme } from './ThemeProvider';
import { LanguageSelector } from './LanguageSelector';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { LOGO_TRANSPARENT } from '../assets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Layout = ({ children }) => {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [sendingContact, setSendingContact] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isCustomer = user?.role === 'customer';
  const isBusiness = user?.role === 'business';

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error(t('contact_fill_all_fields'));
      return;
    }
    
    setSendingContact(true);
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      if (response.data.success) {
        toast.success(t('contact_sent_success'));
        setContactForm({ name: '', email: '', message: '' });
        setContactModalOpen(false);
      } else {
        toast.error('Error al enviar el mensaje');
      }
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Error al enviar el mensaje. Inténtalo de nuevo.');
    } finally {
      setSendingContact(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/70 dark:bg-black/60 backdrop-blur-xl border-b border-white/20">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo + BETA Badge */}
            <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="logo-link">
              <img 
                src={LOGO_TRANSPARENT} 
                alt="LokAlGrow" 
                className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto object-contain"
              />
              <span className="bg-amber-500 text-white text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                Beta
              </span>
            </Link>

            {/* Mobile: Language selector + Menu button */}
            <div className="md:hidden flex items-center gap-2">
              <LanguageSelector />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="h-9 w-9"
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              {/* Public Links - visible to all */}
              <Link to="/negocios" className="text-sm font-medium hover:text-local-sage transition-colors" data-testid="nav-directory">
                {t('nav_businesses')}
              </Link>
              <Link to="/kupons" className="text-sm font-medium hover:text-local-sage transition-colors" data-testid="nav-kupons">
                {t('nav_vip_offers')}
              </Link>
              
              {user ? (
                <>
                  {isCustomer && (
                    <>
                      <Link to="/profile" className="text-sm font-medium hover:text-local-sage transition-colors" data-testid="nav-profile">
                        {t('my_profile')}
                      </Link>
                    </>
                  )}
                  {isBusiness && (
                    <>
                      {/* Prominent Create Kupon Button */}
                      <Link to="/business/kupons?action=create" data-testid="nav-create-kupon">
                        <Button className="bg-local-sage hover:bg-local-sage/90 rounded-full gap-2" size="sm">
                          <Plus className="w-4 h-4" />
                          {t('nav_create_offer')}
                        </Button>
                      </Link>
                      <Link to="/business/dashboard" className="text-sm font-medium hover:text-local-sage transition-colors" data-testid="nav-business-dashboard">
                        {t('dashboard')}
                      </Link>
                      <Link to="/business/kupons" className="text-sm font-medium hover:text-local-sage transition-colors" data-testid="nav-business-kupons">
                        {t('nav_my_offers')}
                      </Link>
                      <Link to="/business/reviews" className="text-sm font-medium hover:text-local-sage transition-colors" data-testid="nav-business-reviews">
                        {t('reviews')}
                      </Link>
                      <Link to="/business/campaigns" className="text-sm font-medium hover:text-local-sage transition-colors" data-testid="nav-campaigns">
                        {t('campaigns')}
                      </Link>
                    </>
                  )}
                  <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
                    {t('logout')}
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" data-testid="nav-login">
                    <Button variant="ghost" size="sm">{t('login')}</Button>
                  </Link>
                  <Link to="/register" data-testid="nav-register">
                    <Button className="bg-local-sage hover:bg-local-sage/90 rounded-full" size="sm">
                      {t('register')}
                    </Button>
                  </Link>
                </>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="ml-2"
                data-testid="theme-toggle"
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>
              <LanguageSelector />
            </nav>

            {/* Mobile: Clean minimal header - only hamburger menu */}
            {/* REMOVED - Now in the header section above */}
          </div>
        </div>

        {/* Mobile Menu - Full screen overlay using Portal to ensure it's above everything */}
        {createPortal(
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="md:hidden fixed inset-0"
                style={{ backgroundColor: '#000000', zIndex: 99999 }}
                data-testid="mobile-menu"
              >
                {/* Close button */}
                <div className="flex justify-between items-center p-4 border-b border-gray-800">
                  <div className="flex items-center gap-2">
                    <img 
                      src={LOGO_TRANSPARENT} 
                      alt="LokAlGrow" 
                      className="h-8 w-auto"
                    />
                    <span className="bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                      Beta
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setMobileMenuOpen(false)}
                    className="h-10 w-10 text-white hover:bg-white/10"
                  >
                    <X className="w-6 h-6" />
                  </Button>
                </div>
                
                <nav className="px-6 py-6 flex flex-col h-[calc(100%-72px)] overflow-y-auto">
                  {/* Main Navigation Links */}
                  <div className="space-y-1">
                    <Link 
                      to="/negocios" 
                      className="block py-4 text-xl font-medium text-white border-b border-gray-800" 
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav_businesses')}
                    </Link>
                    <Link 
                      to="/kupons" 
                      className="block py-4 text-xl font-medium text-white border-b border-gray-800" 
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav_vip_offers')}
                    </Link>
                    <Link 
                      to="/rewards" 
                      className="block py-4 text-xl font-medium text-white border-b border-gray-800" 
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('my_rewards')}
                    </Link>
                    <Link 
                      to="/para-negocios" 
                      className="block py-4 text-xl font-medium text-amber-500 border-b border-gray-800" 
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('for_businesses') || 'Para Negocios'}
                    </Link>
                  </div>
                  
                  {user ? (
                    <div className="mt-6 space-y-1">
                      {isCustomer && (
                        <Link 
                          to="/profile" 
                          className="block py-4 text-xl font-medium text-white border-b border-gray-800" 
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {t('my_profile')}
                        </Link>
                      )}
                      {isBusiness && (
                        <>
                          <Link 
                            to="/business/dashboard" 
                            className="block py-4 text-xl font-medium text-white border-b border-gray-800" 
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {t('dashboard')}
                          </Link>
                          <Link 
                            to="/business/kupons" 
                            className="block py-4 text-xl font-medium text-white border-b border-gray-800" 
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {t('nav_my_offers')}
                          </Link>
                          <Link to="/business/kupons?action=create" onClick={() => setMobileMenuOpen(false)}>
                            <Button className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700 rounded-xl h-14 text-lg gap-2">
                              <Plus className="w-5 h-5" />
                              {t('nav_create_offer')}
                            </Button>
                          </Link>
                        </>
                      )}
                      <button 
                        onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                        className="block w-full py-4 text-xl font-medium text-red-500 text-left mt-4"
                      >
                        {t('logout')}
                      </button>
                    </div>
                  ) : (
                    <div className="mt-8 space-y-4">
                      <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="outline" className="w-full h-14 rounded-xl text-lg border-2 border-gray-600 text-white hover:bg-white/10">
                          {t('login')}
                        </Button>
                      </Link>
                      <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                        <Button className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-lg">
                          {t('register')}
                        </Button>
                      </Link>
                    </div>
                  )}
                  
                  {/* Settings at bottom - Only theme toggle (language is in header) */}
                  <div className="mt-auto pt-8 border-t border-gray-800">
                    <div className="flex items-center justify-between">
                      <span className="text-base text-gray-400">{t('theme') || 'Tema'}</span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={toggleTheme}
                        className="h-10 w-10 text-white hover:bg-white/10"
                      >
                        {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                      </Button>
                    </div>
                  </div>
                </nav>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
      </header>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-muted/30 border-t mt-12 sm:mt-24">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 lg:gap-12">
            <div className="col-span-2 sm:col-span-1">
              <h3 className="font-manrope font-bold text-base sm:text-lg mb-3 sm:mb-4">LokalGrow</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {t('footer_tagline')}
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-sm sm:text-base mb-3 sm:mb-4">{t('nav_vip_offers')}</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-muted-foreground">
                <li><Link to="/kupons" className="hover:text-local-sage transition-colors">{t('explore_offers')}</Link></li>
                <li><Link to="/rewards" className="hover:text-local-sage transition-colors">{t('my_rewards')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm sm:text-base mb-3 sm:mb-4">Legal</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-muted-foreground">
                <li><Link to="/legal/privacy" className="hover:text-local-sage transition-colors">{t('privacy_policy')}</Link></li>
                <li><Link to="/legal/terms" className="hover:text-local-sage transition-colors">{t('terms_conditions')}</Link></li>
                <li><Link to="/legal/cookies" className="hover:text-local-sage transition-colors">Cookies</Link></li>
              </ul>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <h4 className="font-semibold text-sm sm:text-base mb-3 sm:mb-4">{t('contact')}</h4>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                {t('have_questions')}
              </p>
              <button
                onClick={() => setContactModalOpen(true)}
                className="inline-flex items-center gap-2 text-xs sm:text-sm text-local-sage hover:underline font-medium"
                data-testid="footer-contact-btn"
              >
                <Mail className="w-4 h-4" />
                {t('contact_us')}
              </button>
            </div>
          </div>
          <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t text-center text-xs sm:text-sm text-muted-foreground">
            <p>© 2025 LokalGrow. {t('footer_tagline')}.</p>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      <AnimatePresence>
        {contactModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[100]"
              onClick={() => setContactModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed left-4 right-4 sm:left-1/2 sm:right-auto top-4 bottom-4 sm:top-1/2 sm:bottom-auto sm:-translate-y-1/2 sm:-translate-x-1/2 w-auto sm:w-full sm:max-w-md bg-card rounded-2xl shadow-lg z-[101] flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[90vh]"
              data-testid="contact-modal"
            >
              <div className="flex items-center justify-between p-6 pb-4 border-b">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5 text-local-sage" />
                  {t('contact_support')}
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setContactModalOpen(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <form onSubmit={handleContactSubmit} className="flex-1 overflow-y-auto p-6 pt-4 space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">{t('name')}</label>
                  <Input
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder={t('your_name')}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">{t('email')}</label>
                  <Input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder={t('your_email')}
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">{t('message')}</label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder={t('contact_message_placeholder')}
                    className="w-full min-h-[100px] rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-local-sage"
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-local-sage hover:bg-local-sage/90"
                  disabled={sendingContact}
                  data-testid="contact-submit-btn"
                >
                  {sendingContact ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin">⏳</span>
                      {t('sending')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      {t('send_message')}
                    </span>
                  )}
                </Button>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
